import { useSelector, useDispatch } from "react-redux";
import { setUser, initialState } from "store/auth/userSlice";
// import { apiSignOut } from 'services/AuthService'
import { useNavigate } from "react-router-dom";
import appConfig from "configs/app.config";
import { signInUserData } from "../../mock/data/authData";
// import { signUpapi, signIn as signInAPI } from 'services/LiveApi'
import { logIn, createNewUser } from "services/authentication";
import { onSignInSuccess, onSignOutSuccess } from "store/auth/sessionSlice";
import { REDIRECT_URL_KEY } from "constants/app.constant";
import useQuery from "./useQuery";
import axios from "axios";
import config from "utils/apiEndPoint";
import { enrichAuthUserFromProfile } from "constants/adminEmployeeMgmt.constant";
import { loadAdminEmployeeSession } from "services/AdminEmployeeProfileService";
import { jwtDecode } from "jwt-decode";

/** Readable message from axios/API errors (DRF often uses `detail`, `message`, or field arrays). */
export function getAuthApiErrorMessage(error, fallback = "Sign in failed. Please try again.") {
  const data = error?.response?.data;
  if (data && typeof data === "object") {
    if (typeof data.detail === "string" && data.detail.trim()) {
      return data.detail.trim();
    }
    if (Array.isArray(data.detail) && data.detail.length) {
      return data.detail
        .map((x) => (typeof x === "string" ? x : String(x)))
        .join(" ");
    }
    if (typeof data.message === "string" && data.message.trim()) {
      return data.message.trim();
    }
    if (Array.isArray(data.non_field_errors) && data.non_field_errors.length) {
      return data.non_field_errors.map(String).join(" ");
    }
    const fieldKey = Object.keys(data).find((k) => Array.isArray(data[k]));
    if (fieldKey && data[fieldKey]?.length) {
      return data[fieldKey].map(String).join(" ");
    }
  }
  const msg = error?.message;
  if (typeof msg === "string" && msg.trim() && !/^AxiosError/i.test(msg)) {
    return msg.trim();
  }
  return fallback;
}

function useAuth() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = useQuery();
  const { token, signedIn } = useSelector((state) => state.auth.session);

  const signIn = async (values) => {
    try {
      // Dev-only bypass: mock mode ON ho to API hit na ho.
      if (appConfig.enableMock) {
        const mockUser = signInUserData?.[0];

        if (
          values?.email === mockUser?.email &&
          values?.password === mockUser?.password
        ) {
          const accessToken = "dev-access-token";
          const refreshToken = "dev-refresh-token";

          // Project ke different parts alag keys use kar rahe hain.
          localStorage.setItem("accessToken", accessToken);
          localStorage.setItem("refreshToken", refreshToken);
          localStorage.setItem("access-token", accessToken);
          localStorage.setItem("refresh-token", refreshToken);

          // ProtectedRoute ko authenticated true chahiye.
          dispatch(onSignInSuccess(accessToken));

          dispatch(
            setUser({
              ...mockUser,
              permissions: [],
              is_staff: true,
              is_superuser: true,
            })
          );

          const redirectUrl = query.get(REDIRECT_URL_KEY);
          navigate(
            redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath
          );

          return { status: "success", message: "Welcome to shipclues" };
        }

        return {
          status: "failed",
          message: "Invalid email or password",
        };
      }

      const resp = await logIn(values);
      const body = resp?.data;
      const accessToken =
        body?.access || body?.access_token || body?.token || null;
      const refreshTokenVal = body?.refresh || body?.refresh_token;

      if (!accessToken) {
        return {
          status: "failed",
          message:
            body?.message ||
            body?.detail ||
            "Login failed: no access token in response",
        };
      }

      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("access-token", accessToken);
      if (refreshTokenVal) {
        localStorage.setItem("refresh-token", refreshTokenVal);
      }

      dispatch(onSignInSuccess(accessToken));

      let profileWarning = null;
      let sessionUser = null;
      let decodedToken = null;

      try {
        decodedToken = jwtDecode(accessToken);
      } catch {
        decodedToken = null;
      }

      const isAdminEmployeeLogin =
        decodedToken?.user_type === "admin_employee" ||
        String(config?.authentication?.signIn || "").includes("admin-employee");

      if (isAdminEmployeeLogin) {
        const session = await loadAdminEmployeeSession({
          email: values.email,
          user_type: decodedToken?.user_type || "admin_employee",
          id: decodedToken?.user_id,
        });

        if (session.ok) {
          sessionUser = enrichAuthUserFromProfile({
            ...session.user,
            is_staff: body?.is_staff || false,
            is_superuser: body?.is_superuser || false,
          });
        } else {
          profileWarning = session.message;
          sessionUser = enrichAuthUserFromProfile({
            ...session.user,
            email: values.email,
            is_staff: body?.is_staff || false,
            is_superuser: body?.is_superuser || false,
          });
        }
      }

      if (!sessionUser) {
        let user = body?.user || null;
        if (!user) {
          try {
            const profileRes = await axios.get(config.profile.info, {
              headers: { Authorization: `Bearer ${accessToken}` },
            });
            const pd = profileRes?.data;
            user =
              (pd && typeof pd === "object" && pd.data !== undefined
                ? pd.data
                : pd) || null;
          } catch {
            user = { email: values.email };
          }
        }

        let permissions = [];
        const isSuperuser = user?.is_superuser || body?.is_superuser || false;
        const isStaff = user?.is_staff || body?.is_staff || false;
        const userId =
          user?.id || user?.user_id || user?._id || body?.user_id || body?.id;
        const userEmail = user?.email || body?.email;

        if (!isSuperuser) {
          try {
            const requestConfig = {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            };

            if (userId && userEmail) {
              requestConfig.params = {
                user_id: userId,
                email: userEmail,
              };
            }

            const permResponse = await axios.get(
              config.admin_user.uiPermissions,
              requestConfig
            );

            let permissionList = [];
            if (permResponse.data) {
              if (Array.isArray(permResponse.data)) {
                permissionList = permResponse.data;
              } else if (permResponse.data.data) {
                if (Array.isArray(permResponse.data.data)) {
                  permissionList = permResponse.data.data;
                } else if (
                  permResponse.data.data.byKey &&
                  typeof permResponse.data.data.byKey === "object"
                ) {
                  permissionList = Object.values(permResponse.data.data.byKey);
                }
              } else if (
                permResponse.data.results &&
                Array.isArray(permResponse.data.results)
              ) {
                permissionList = permResponse.data.results;
              } else if (
                permResponse.data.byKey &&
                typeof permResponse.data.byKey === "object"
              ) {
                permissionList = Object.values(permResponse.data.byKey);
              }
            }

            permissionList.forEach((item) => {
              if (item && item.permission_key) {
                permissions.push({
                  permission_key: item.permission_key,
                  can_view: !!item.can_view,
                  can_create: !!item.can_create,
                  can_update: !!item.can_update,
                  module_name: item.module_name || null,
                  module_code: item.module_code || null,
                });
              }
            });
          } catch {
            // Legacy permissions fetch failed — login still succeeds with empty access
          }
        }

        sessionUser = enrichAuthUserFromProfile({
          ...user,
          permissions,
          is_staff: isStaff,
          is_superuser: isSuperuser,
        });
      }

      if (sessionUser) {
        dispatch(setUser(sessionUser));
      }

      const redirectUrl = query.get(REDIRECT_URL_KEY);
      navigate(redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath);

      return {
        status: "success",
        message: profileWarning
          ? `Welcome. ${profileWarning}`
          : "Welcome to shipclues",
        profileWarning,
      };
    } catch (errors) {
      return {
        status: "failed",
        message: getAuthApiErrorMessage(errors),
      };
    }
  };

  // Signup api calling
  // Improved signUp function for useAuth hook
  // const signUp = async (values) => {
  //     try {
  //         const resp = await createNewUser(values)

  //         // Check if the API call was successful
  //         if (resp && (resp.status === 201 || resp.data)) {
  //             // Only dispatch and navigate if you want immediate login after signup
  //             // If you want to redirect to sign-in page instead, comment out the lines below

  //             if (resp.data && resp.data.token) {
  //                 const { token } = resp.data
  //                 dispatch(onSignInSuccess(token))
  //                 if (resp.data.user) {
  //                     dispatch(
  //                         setUser(
  //                             resp.data.user || {
  //                                 email: '',
  //                                 password: '',
  //                             }
  //                         )
  //                     )
  //                 }
  //                 const redirectUrl = query.get(REDIRECT_URL_KEY)
  //                 navigate(
  //                     redirectUrl ? redirectUrl : appConfig. unAuthenticatedEntryPath
  //                 )
  //             }

  //             return {
  //                 status: 201,
  //                 data: {
  //                     message: resp.data?.message || "Account created successfully"
  //                 }
  //             }
  //         } else {
  //             return {
  //                 status: 'failed',
  //                 message: 'Signup failed. Please try again.',
  //             }
  //         }
  //     } catch (errors) {
  //         return {
  //             status: 'failed',
  //             message: errors?.response?.data?.message || errors.toString(),
  //         }
  //     }
  // }

  const signUp = async (values) => {
    try {
      const resp = await createNewUser(values);
      console.log("createNewUser response:", resp);

      if (resp.data && resp.status === 201) {
        // Only set user data, don't dispatch token since API doesn't return it
        dispatch(setUser(resp.data));

        // Don't navigate here - let the SignUpForm handle navigation
        // The SignUpForm wants to show a success message first

        return {
          status: 201,
          message: "User created successfully",
          data: resp.data,
        };
      }

      // If we reach here, something went wrong
      return {
        status: "failed",
        message: "Unexpected response format",
      };
    } catch (errors) {
      console.error("SignUp error:", errors);
      return {
        status: "failed",
        message: getAuthApiErrorMessage(errors, "Unable to create account. Please try again."),
      };
    }
  };

  const handleSignOut = () => {
    dispatch(onSignOutSuccess());
    dispatch(setUser(initialState));
    navigate(appConfig.unAuthenticatedEntryPath);
  };

  const signOut = async () => {
    // await apiSignOut()
    handleSignOut();
  };

  return {
    authenticated: token && signedIn,
    signIn,
    signUp,
    signOut,
  };
}

export default useAuth;

